"use client";

import { motion } from "framer-motion";
import {
  Award,
  Users,
  Globe,
  Shield,
  Target,
  Eye,
  Heart,
  CheckCircle,
} from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

const About = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const stats = [
    { icon: Award, value: "15+", label: "Years Experience" },
    { icon: Users, value: "5000+", label: "Certified Seafarers" },
    { icon: Globe, value: "50+", label: "Countries Served" },
    { icon: Shield, value: "100%", label: "STCW Compliant" },
  ];

  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description:
        "Help shipowners, managers, seafarers and charterers navigate constant change with confidence through practical maritime solutions.",
    },
    {
      icon: Eye,
      title: "Our Vision",
      description:
        "To be the leading maritime services provider, recognized for operational excellence, regulatory compliance, and long-term client success.",
    },
    {
      icon: Heart,
      title: "Our Values",
      description:
        "Understand reality. Offer practical options. Take responsibility. Clear communication, transparent reporting, and respect for operational constraints.",
    },
  ];

  const services = [
    {
      title: "Training & Certifications",
      description:
        "Structured learning paths for seafarers and end-to-end support for maritime and offshore certifications.",
    },
    {
      title: "Ship Management",
      description:
        "Technical and commercial management services designed to protect your asset and support long-term performance.",
    },
    {
      title: "Crew Management",
      description:
        "Recruitment, planning, documentation, payroll and welfare with safety and retention as core focus.",
    },
    {
      title: "Technical Services",
      description:
        "Ship preparation, inspections, modifications and ongoing technical support.",
    },
    {
      title: "Registry & Survey",
      description:
        "Guidance and coordination for flag registration and change of flag services.",
    },
    {
      title: "Ship Insurance",
      description:
        "Assistance in arranging and managing marine insurances with appropriate cover levels.",
    },
    {
      title: "Ship Chartering",
      description:
        "Connecting cargo and tonnage with practical, sustainable employment solutions.",
    },
    {
      title: "Ship Brokering",
      description:
        "Sale, purchase and long-term vessel decisions with confidentiality and expertise.",
    },
  ];

  const locations = [
    {
      office: "Dubai",
      country: "United Arab Emirates",
      focus: "Regional Coordination & Support",
      email: "dubai@nautilustimes.com",
    },
    {
      office: "Thailand",
      country: "Bangkok & Region",
      focus: "Training & Certifications",
      email: "thailand@nautilustimes.com",
    },
    {
      office: "India",
      country: "India",
      focus: "Seafarer Support & Recruitment",
      email: "india@nautilustimes.com",
    },
    {
      office: "Singapore",
      country: "Singapore",
      focus: "Regional Coordination & Support",
      email: "singapore@nautilustimes.com",
    },
    {
      office: "Hong Kong",
      country: "Hong Kong",
      focus: "Training & Certifications",
      email: "hongkong@nautilustimes.com",
    },
    {
      office: "Greece",
      country: "Greece",
      focus: "Seafarer Support & Recruitment",
      email: "greece@nautilustimes.com",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-16 text-white md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="mb-4 text-3xl font-bold leading-tight sm:text-4xl md:mb-6 md:text-5xl lg:text-6xl">
              Nautilus Times
            </h1>
            <p className="mx-auto max-w-3xl text-base text-teal-300 sm:text-lg md:mb-6 md:text-xl">
              Maritime People. Practical Solutions.
            </p>
            <p className="mx-auto max-w-3xl text-base text-slate-300 sm:text-lg md:text-xl">
              A maritime services and training group focused on helping
              shipowners, managers, seafarers and charterers navigate constant
              change with confidence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-4 sm:gap-8 md:grid-cols-4">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 sm:mb-4 sm:h-16 sm:w-16">
                  <stat.icon className="h-6 w-6 text-teal-600 sm:h-8 sm:w-8" />
                </div>
                <div className="mb-1 text-2xl font-bold text-slate-900 sm:mb-2 sm:text-3xl">
                  {stat.value}
                </div>
                <div className="text-xs text-slate-600 sm:text-sm md:text-base">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 grid grid-cols-1 items-center gap-8 md:mb-20 lg:grid-cols-2 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:mb-6 md:text-4xl lg:text-5xl">
                Our Approach
              </h2>
              <p className="mb-4 text-base text-slate-600 sm:text-lg md:mb-6">
                We exist to help shipowners, managers, seafarers and charterers
                navigate constant change with confidence. The sea is never the
                same two days in a row – and neither is shipping. Our approach
                is simple and practical.
              </p>
              <div className="space-y-4 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="mt-0.5 h-6 w-6 flex-shrink-0 text-teal-600" />
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Understand the Reality
                    </h4>
                    <p className="text-sm text-slate-600">
                      Of your operation and constraints
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="mt-0.5 h-6 w-6 flex-shrink-0 text-teal-600" />
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Offer Practical Options
                    </h4>
                    <p className="text-sm text-slate-600">
                      Not theory – real solutions that work
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="mt-0.5 h-6 w-6 flex-shrink-0 text-teal-600" />
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Take Responsibility
                    </h4>
                    <p className="text-sm text-slate-600">
                      For execution and follow-up
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-base text-slate-600 sm:text-lg">
                We combine operational experience at sea and ashore with strong
                understanding of regulations, flag requirements and commercial
                pressures. For our clients, this means fewer surprises, smoother
                audits and a more predictable business.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative order-1 lg:order-2"
            >
              <img
                src="/ship.png"
                alt="Maritime Training Facility"
                className="rounded-2xl shadow-2xl"
                height={1000}
                width={1000}
              />
              <div className="absolute -bottom-4 -right-4 rounded-xl bg-white p-4 shadow-lg sm:-bottom-6 sm:-right-6 sm:p-6">
                <div className="text-xl font-bold text-slate-900 sm:text-2xl">
                  Established
                </div>
                <div className="text-sm text-slate-600 sm:text-base">
                  Maritime Excellence
                </div>
              </div>
            </motion.div>
          </div>

          {/* Mission, Vision, Values */}
          <div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-3">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-2xl bg-white p-6 text-center shadow-lg sm:p-8"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 sm:mb-6 sm:h-16 sm:w-16">
                  <value.icon className="h-6 w-6 text-teal-600 sm:h-8 sm:w-8" />
                </div>
                <h3 className="mb-3 text-lg font-bold text-slate-900 sm:mb-4 sm:text-xl">
                  {value.title}
                </h3>
                <p className="text-sm text-slate-600 sm:text-base">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Offices */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center md:mb-16"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:mb-6 md:text-4xl lg:text-5xl">
              Global Footprint
            </h2>
            <p className="mx-auto max-w-3xl text-base text-slate-600 sm:text-lg md:text-xl">
              Operating through authorised offices in key maritime hubs with
              local understanding
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {locations.map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-xl bg-slate-50 p-8 text-center shadow-lg"
              >
                <Globe className="mx-auto mb-4 h-8 w-8 text-teal-600" />
                <h3 className="mb-2 text-lg font-bold text-slate-900">
                  {location.office}
                </h3>
                <p className="mb-4 text-sm text-slate-600">
                  {location.country}
                </p>
                <p className="mb-4 text-xs font-semibold text-teal-600">
                  {location.focus}
                </p>
                <a
                  href={`mailto:${location.email}`}
                  className="text-sm text-teal-600 hover:text-teal-700 underline"
                >
                  {location.email}
                </a>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 rounded-2xl bg-teal-50 p-8 text-center"
          >
            <p className="mx-auto max-w-3xl text-slate-700">
              Together, these offices ensure that clients are never too far from
              a Nautilus Times representative who understands their context and
              can provide real-time support.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center md:mb-16"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:mb-6 md:text-4xl lg:text-5xl">
              Our Connected Services
            </h2>
            <p className="mx-auto max-w-3xl text-base text-slate-600 sm:text-lg md:text-xl">
              A full range of maritime services under one umbrella
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-4">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-xl bg-white p-4 transition-colors duration-300 hover:bg-teal-50 sm:p-6"
              >
                <h3 className="mb-2 text-base font-semibold text-slate-900 sm:mb-3 sm:text-lg">
                  {service.title}
                </h3>
                <p className="text-sm text-slate-600 sm:text-base">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-center text-white md:p-16"
          >
            <h2 className="mb-4 text-2xl font-bold sm:text-3xl md:mb-6 md:text-4xl">
              Ready to Work with Us?
            </h2>
            <p className="mx-auto mb-8 max-w-3xl text-base text-slate-300 sm:text-lg md:mb-10">
              Contact our team today to discuss how Nautilus Times can support
              your maritime operations.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 rounded-lg bg-teal-600 px-6 py-3 font-semibold text-white transition-colors duration-300 hover:bg-teal-700 sm:px-8 sm:py-4"
            >
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
